// src/app/core/shared-services/gtm.service.ts
// Aligned with .cursor/context/implementation_ready_reconciled_matrix.csv.
// All event emissions MUST go through track* methods on this service. The raw
// dataLayer push is private (`_push`) so feature components cannot emit ad-hoc
// events that drift from the matrix.
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GTM_CONSTANTS, GtmDataLayerPlanType } from './gtm/gtm.constants';
import {
  GTM_EVENTS,
  GtmDataLayerEvent,
  GtmPushEventName,
  buildEcommerceItem,
  buildDiscountInfo,
  GtmEcommerceItem,
} from './gtm/gtm.events';

interface BasePayloadOptions {
  funnelName?: string;
  featureGrouping?: string;
  items?: GtmEcommerceItem[];
  /**
   * Adds `value` + `currency` to the ecommerce block; `'items'` derives the value from the
   * item subtotal. `null` emits an explicit null — an unknown amount, never a zero one.
   */
  value?: number | 'items' | null;
  /** Adds `transaction_id` to the ecommerce block. */
  transactionId?: string;
  /** Use `page_url` + `page_title` instead of `page_location` + `page_title` (updated tags). */
  usePageUrl?: boolean;
  /** Omit any page_* fields when the tag spec does not include them. */
  omitPageContext?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GtmService {
  public emailHash: string = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('EmailHash') : null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
  ) {
    this.initPageViewTracking();
  }

  private initPageViewTracking(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateSessionContext();
        this.trackPageView();
        const url = event.urlAfterRedirects ?? event.url ?? '';
        const isPackages = url.includes('packages') || (typeof window !== 'undefined' && window.location.pathname.includes('packages'));
        if (isPackages) {
          this.trackViewPackages();
        }
      });
  }

  /**
   * Internal raw push. Kept private to centralise the contract — feature
   * components must use the track* methods. The Phase-4 guard test asserts
   * that this surface stays internal.
   */
  private _push(event: GtmPushEventName, payload: Record<string, unknown>): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }

  /** Update session context (currentURL, previousURL) */
  public updateSessionContext(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const current = sessionStorage.getItem('currentURL');
      if (current && current !== window.location.href) {
        sessionStorage.setItem('previousURL', current);
      }
      sessionStorage.setItem('currentURL', window.location.href);
    } catch (e) {
      console.error('GTM session context update failed:', e);
    }
  }

  /** Common payload shared by every event. */
  private basePayload(options: BasePayloadOptions = {}): Record<string, unknown> {
    const { funnelName, featureGrouping, items, value, transactionId, usePageUrl, omitPageContext } = options;
    const href = typeof window !== 'undefined' ? window.location.href : '';
    const title = typeof document !== 'undefined' ? document.title : '';
    const p: Record<string, unknown> = {
      digital_channel: GTM_CONSTANTS.DIGITAL_CHANNEL,
      customer_type: GTM_CONSTANTS.CUSTOMER_TYPE,
      line_of_business: GTM_CONSTANTS.LINE_OF_BUSINESS,
      feature_grouping: featureGrouping ?? GTM_CONSTANTS.FEATURE_GROUPING_SALES,
      plan_type: this.getPlanType(),
    };
    if (!omitPageContext) {
      if (usePageUrl) {
        p.page_url = href;
        p.page_title = title;
      } else {
        p.page_location = href;
        p.page_title = title;
      }
    }
    if (funnelName) p.funnel_name = funnelName;
    const ecommerce = this.buildEcommerce(items, value, transactionId);
    if (ecommerce) p.ecommerce = ecommerce;
    return p;
  }

  private buildEcommerce(
    items: GtmEcommerceItem[] | undefined,
    value: number | 'items' | null | undefined,
    transactionId: string | undefined
  ): Record<string, unknown> | undefined {
    if (!items?.length && value === undefined && transactionId == null) return undefined;
    return {
      ...(transactionId != null && { transaction_id: transactionId }),
      ...(value !== undefined && {
        value: value === 'items' ? this.itemsSubtotal(items) : value,
        currency: GTM_CONSTANTS.CURRENCY,
      }),
      ...(items?.length ? { items } : {}),
    };
  }

  /**
   * Current dataLayer `plan_type` for downstream events. Falls back to the
   * spec placeholder `Postpaid/Prepaid` until the user actually picks one
   * via `trackSelectPlanType` (or another flow stores a value).
   */
  public getPlanType(): GtmDataLayerPlanType | typeof GTM_CONSTANTS.DATALAYER_PLAN_TYPE_DEFAULT {
    if (typeof sessionStorage === 'undefined') return GTM_CONSTANTS.DATALAYER_PLAN_TYPE_DEFAULT;
    const stored = sessionStorage.getItem(GTM_CONSTANTS.PLAN_TYPE_SESSION_KEY);
    if (stored === 'Postpaid' || stored === 'Prepaid') return stored;
    // Infer from AddedCartItem when plan type was never explicitly selected
    try {
      const raw = sessionStorage.getItem('AddedCartItem');
      if (raw) {
        const items = JSON.parse(raw);
        const first = Array.isArray(items) ? items[0] : items;
        const core = first?.CoreOfferable ?? first;
        const isRecurring = core?.Billing_IsRecurring;
        if (isRecurring != null) return isRecurring ? 'Postpaid' : 'Prepaid';
      }
    } catch { /* ignore */ }
    return GTM_CONSTANTS.DATALAYER_PLAN_TYPE_DEFAULT;
  }

  /** Persist the dataLayer `plan_type` for downstream events. */
  public setPlanType(planType: GtmDataLayerPlanType): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(GTM_CONSTANTS.PLAN_TYPE_SESSION_KEY, planType);
    } catch (e) {
      console.error('GTM plan_type persistence failed:', e);
    }
  }

  /** ecommerce.value for a set of items — price × quantity, summed. Null when there is nothing to sum. */
  private itemsSubtotal(items: GtmEcommerceItem[] | undefined): number | null {
    if (!items?.length) return null;
    return items.reduce(
      (total, it) => total + (Number(it?.price) || 0) * (Number(it?.quantity) || 0),
      0
    );
  }

  /** Flat product dimensions for lead / journey tags (not nested under ecommerce). */
  private productDimsFromItem(it: GtmEcommerceItem): Record<string, string> {
    // console.log('Product Dimensions:', it);
    return {
      product_id: it.item_id,
      product_name: it.item_name,
      product_brand: it.item_brand,
      product_category: it.item_category,
      product_variant: it.item_variant,
    };
  }

  // ─── Sales Funnel ─────────────────────────────────────────────────────────

  trackAddToCart(pkg: any): void {
    this.updateSessionContext();
    const item = buildEcommerceItem(pkg);
    this._push(GTM_EVENTS.ADD_TO_CART, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items: [item] }),
      step_name: 'Package Selected',
      feature_function: 'Add to Cart',
    });
  }

  trackViewCart(items: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.VIEW_CART, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'My Cart',
      feature_function: 'View Cart',
    });
  }

  trackBeginCheckout(items: GtmEcommerceItem[], value?: number): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.BEGIN_CHECKOUT, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL,
        items,
        value: value ?? 'items',
        usePageUrl: true,
      }),
      step_name: 'Checkout',
      feature_function: 'Begin Checkout',
    });
  }

  trackCheckoutView(items: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.CHECKOUT_VIEW, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Checkout',
      feature_function: 'Checkout',
    });
  }

  trackFunnelStepBack(stepFrom: string, stepTo: string, items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.FUNNEL_STEP_BACK, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
      step_name: stepFrom,
      step_from: stepFrom,
      step_to: stepTo,
      navigation_method: 'back_button',
      feature_function: 'Back Navigation',
    });
  }

  trackCheckoutButton(btnType: 'backBtn' | 'addPaymentBtn' | 'editCartBtn', items?: GtmEcommerceItem[]): void {
    if (btnType === 'backBtn') {
      this.trackFunnelStepBack('Checkout', 'My Cart', items);
    } else if (btnType === 'editCartBtn') {
      this.trackEditCart(items);
    } else {
      this._push(GTM_EVENTS.ADD_PAYMENT_INFO, {
        ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
        step_name: 'Checkout',
        feature_function: 'Payment Method',
        payment_type: 'debit_order',
      });
    }
  }

  trackCheckoutFail(errorCode: string | null, errorMessage: string | null): void {
    this._push(GTM_EVENTS.PURCHASE_FAILED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Checkout',
      feature_function: 'Checkout Failure',
      error_code: errorCode ?? '',
      error_message: errorMessage ?? '',
    });
  }

  trackCartButton(btnName: 'deleteCartBtn' | 'backBtn', items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    if (btnName === 'deleteCartBtn') {
      this._push(GTM_EVENTS.REMOVE_FROM_CART, {
        ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
        step_name: 'My Cart',
        feature_function: 'Remove from Cart',
      });
    } else {
      this.trackFunnelStepBack('My Cart', 'Select Package', items);
    }
  }

  trackEditCart(items?: GtmEcommerceItem[]): void {
    this._push(GTM_EVENTS.EDIT_CART, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Checkout',
      feature_function: 'Edit Cart',
    });
  }

  trackPurchase(
    transactionId: string,
    value: number,
    items: GtmEcommerceItem[],
    options?: { step_name?: string; feature_function?: string; payment_type?: string; payment_gateway?: string }
  ): void {
    const {
      step_name = 'Completed',
      feature_function = 'Purchase Complete',
      payment_type,
      payment_gateway,
    } = options ?? {};
    const txId = transactionId ?? '';
    this.updateSessionContext();
    this._push(GTM_EVENTS.PURCHASE, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL,
        items,
        value: value ?? null,
        transactionId: txId,
        usePageUrl: true,
      }),
      step_name,
      feature_function,
      ...(payment_type != null && {
        payment_type,
        ...(payment_gateway != null && { payment_gateway }),
      }),
    });
  }

  trackAcceptDebitOrder(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.ACCEPT_DEBIT_ORDER, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Accept Debit Order',
      feature_function: 'Accept Debit Order',
      payment_type: 'debit_order',
    });
  }

  trackDeclineDebitOrder(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.DECLINE_DEBIT_ORDER, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Decline Debit Order',
      feature_function: 'Decline Debit Order',
      payment_type: 'debit_order',
    });
  }

  trackAcceptDebitOrderView(options?: { items?: GtmEcommerceItem[]; bankName?: string; accountType?: string }): void {
    this.updateSessionContext();
    const { items, bankName, accountType } = options ?? {};
    this._push(GTM_EVENTS.ACCEPT_DEBIT_ORDER_VIEW, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Accept Debit Order',
      feature_function: 'Accept Debit Order',
      payment_type: 'debit_order',
      bank_name: bankName ?? '',
      account_type: (accountType ?? '').toLowerCase(),
    });
  }

  trackCompleteOrderLater(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.COMPLETE_ORDER_LATER, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Accept Debit Order',
      feature_function: 'Complete Order Later',
      payment_type: 'debit_order',
    });
  }

  trackDebitMandateFailed(
    transactionId: string,
    value: number,
    items: GtmEcommerceItem[],
    failureReason = 'credit_vetting_declined'
  ): void {
    this.updateSessionContext();
    const txId = transactionId ?? '';
    this._push(GTM_EVENTS.DEBIT_MANDATE_FAILED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Failed',
      feature_function: 'Purchase Failed',
      payment_type: 'debit_order',
      transaction_id: txId,
      failure_reason: failureReason ?? '',
      currency: GTM_CONSTANTS.CURRENCY,
      value: value ?? null,
      ecommerce: {
        transaction_id: txId,
        value: value ?? null,
        currency: GTM_CONSTANTS.CURRENCY,
        items: items ?? [],
      },
    });
  }

  trackDebitMandatePending(
    transactionId: string,
    value: number,
    items: GtmEcommerceItem[],
    currency = GTM_CONSTANTS.CURRENCY
  ): void {
    this.updateSessionContext();
    const txId = transactionId ?? '';
    this._push(GTM_EVENTS.DEBIT_MANDATE_PENDING, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Debit Mandate Pending',
      feature_function: 'Purchase Failed',
      payment_type: 'debit_order',
      transaction_id: txId,
      currency: currency ?? GTM_CONSTANTS.CURRENCY,
      value: value ?? null,
      ecommerce: {
        transaction_id: txId,
        value: value ?? null,
        currency: currency ?? GTM_CONSTANTS.CURRENCY,
        items: items ?? [],
      },
    });
  }

  trackPurchaseFailedCreditCard(
    transactionId: string,
    value: number,
    items: GtmEcommerceItem[],
    paymentGateway = 'Netcash',
    failureReason = 'payment_declined'
  ): void {
    this.updateSessionContext();
    const txId = transactionId ?? '';
    this._push(GTM_EVENTS.PURCHASE_FAILED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, usePageUrl: true }),
      step_name: 'Completed',
      feature_function: 'Purchase Failed',
      payment_type: 'credit_card',
      payment_gateway: paymentGateway ?? '',
      transaction_id: txId,
      failure_reason: failureReason ?? '',
      ecommerce: {
        transaction_id: txId,
        value: value ?? null,
        currency: GTM_CONSTANTS.CURRENCY,
        items: items ?? [],
      },
    });
  }

  trackPaymentCheckComplete(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.PAYMENT_CHECK_COMPLETE, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Accept Debit Order',
      feature_function: 'Payment Complete',
      payment_type: 'credit_card',
      payment_gateway: 'Netcash',
      payment_status: 'success',
    });
  }

  trackPaymentCheckFailed(items?: GtmEcommerceItem[], errorMessage?: string): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.PAYMENT_CHECK_FAILED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Checkout',
      feature_function: 'Payment Failed',
      payment_type: 'credit_card',
      payment_gateway: 'Netcash',
      payment_status: 'failed',
      error_message: errorMessage ?? '',
    });
  }

  // ─── OTP (debit-order modal flow) ─────────────────────────────────────────

  trackOtpView(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.OTP_VIEW, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
      step_name: 'Accept Debit Order',
      feature_function: 'OTP Verification',
      otp_method: 'email',
    });
  }

  trackOtpSubmit(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.OTP_SUBMIT, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
      step_name: 'Accept Debit Order',
      feature_function: 'OTP Verification',
      otp_method: 'email',
    });
  }

  trackOtpCancelled(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.OTP_CANCELLED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
      step_name: 'Accept Debit Order',
      feature_function: 'OTP Verification',
      otp_method: 'email',
    });
  }

  // ─── eMandate OTP (postpaid flow) ─────────────────────────────────────────

  trackEmandateOtpSubmit(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.EMANDATE_OTP_SUBMIT, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'eMandate OTP',
      feature_function: 'eMandate OTP Submit',
      otp_method: 'email',
      payment_type: 'debit_order',
    });
  }

  trackEmandateOtpResend(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.EMANDATE_OTP_RESEND, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'eMandate OTP',
      feature_function: 'eMandate OTP Resend',
      otp_method: 'email',
      payment_type: 'debit_order',
    });
  }

  trackDebitMandateSuccess(items?: GtmEcommerceItem[]): void {
    this._push(GTM_EVENTS.DEBIT_MANDATE_SUCCESS, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Debit Order Mandate Success',
      feature_function: 'Accept Debit Order',
      payment_type: 'debit_order',
    });
  }

  trackDebitMandateSubmitted(): void {
    this._push(GTM_EVENTS.DEBIT_MANDATE_SUBMITTED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Debit Order Mandate Submitted',
      feature_function: 'Accept Debit Order',
    });
  }

  // ─── Vetting (postpaid) ───────────────────────────────────────────────────

  trackVettingPassed(): void {
    this._push(GTM_EVENTS.VETTING_PASSED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Vetting Passed',
      feature_function: 'Credit Vetting',
    });
  }

  trackVettingFailed(reason?: string): void {
    this._push(GTM_EVENTS.VETTING_FAILED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Vetting Failed',
      feature_function: 'Credit Vetting',
      failure_reason: reason ?? '',
    });
  }

  trackVettingReferred(): void {
    this._push(GTM_EVENTS.VETTING_REFERRED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Vetting Referred',
      feature_function: 'Credit Vetting',
    });
  }

  trackVettingComplete(): void {
    this._push(GTM_EVENTS.VETTING_COMPLETE, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      step_name: 'Vetting Complete',
      feature_function: 'Credit Vetting',
    });
  }

  // ─── Button clicks ────────────────────────────────────────────────────────

  /** Generic CTA click. `buttonName` is the on-screen label, per the tagging spec. */
  trackButtonClick(
    buttonName: string,
    options: { step_name: string; feature_function: string }
  ): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.BUTTON_CLICK, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL }),
      button_name: buttonName,
      step_name: options.step_name,
      feature_function: options.feature_function,
    });
  }

  // ─── Leads ─────────────────────────────────────────────────────────────────

  trackCallMeBackClick(pkg?: any): void {
    const item = pkg ? buildEcommerceItem(pkg) : undefined;
    this._push(GTM_EVENTS.CALL_ME_BACK_CLICK, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_LEAD, omitPageContext: true }),
      step_name: 'Package Selected',
      feature_function: 'Call Me Back',
      ...(item ? this.productDimsFromItem(item) : {}),
    });
  }

  trackShowMyInterest(pkg?: any): void {
    const items = pkg ? [buildEcommerceItem(pkg)] : undefined;
    this._push(GTM_EVENTS.SHOW_MY_INTEREST, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_LEAD, items }),
      step_name: 'Fibre Lead Enquiry Form',
      feature_function: 'Show My Interest',
    });
  }

  trackGenerateLead(leadNumber: string, value: number, productDetails?: GtmEcommerceItem): void {
    this._push(GTM_EVENTS.GENERATE_LEAD, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_LEAD, omitPageContext: true }),
      step_name: 'Lead Submitted',
      feature_function: 'Show My Interest',
      form_id: 'show-my-interest-lead-form',
      form_name: 'Show My Interest',
      lead_number: leadNumber ?? '',
      lead_source: 'Fibre - Show My Interest',
      value: value ?? null,
      currency: GTM_CONSTANTS.CURRENCY,
      ...(productDetails ? this.productDimsFromItem(productDetails) : {}),
    });
  }

  trackCancelledLead(productDetails?: GtmEcommerceItem): void {
    this._push(GTM_EVENTS.CANCELLED_LEAD, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_LEAD, omitPageContext: true }),
      step_name: 'Lead Cancelled',
      feature_function: 'Show My Interest',
      form_id: 'show-my-interest-lead-form',
      form_name: 'Show My Interest',
      exit_action: 'Cancel - Return to Shopping',
      exit_url: 'https://portal.cellc.co.za/packages',
      ...(productDetails ? this.productDimsFromItem(productDetails) : {}),
    });
  }

  trackInitiateCancelOrder(transactionId: string, items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.INITIATE_CANCEL_ORDER, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL,
        featureGrouping: GTM_CONSTANTS.FEATURE_GROUPING_ACCOUNT_MANAGEMENT,
        items,
      }),
      step_name: 'Orders in Progress',
      feature_function: 'Cancel Order',
      transaction_id: transactionId ?? '',
    });
  }

  trackCancelOrder(transactionId: string, cancellationReason: string, items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.CANCEL_ORDER, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL,
        featureGrouping: GTM_CONSTANTS.FEATURE_GROUPING_ACCOUNT_MANAGEMENT,
        items,
      }),
      step_name: 'Order Cancelled',
      feature_function: 'Cancel Order',
      transaction_id: transactionId ?? '',
      cancellation_reason: cancellationReason ?? '',
    });
  }

  // ─── Auth ──────────────────────────────────────────────────────────────────

  trackLoginClick(clickText: string = 'Login', clickUrl?: string): void {
    const href =
      clickUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
    this._push(GTM_EVENTS.LOGIN, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_JOURNEY,
        featureGrouping: GTM_CONSTANTS.FEATURE_GROUPING_AUTH,
        omitPageContext: true,
      }),
      feature_function: 'Login',
      click_url: href,
      click_text: clickText,
    });
  }

  trackLogin(emailHash: string): void {
    const uid = emailHash ?? (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('EmailHash') : null) ?? '';
    this._push(GTM_EVENTS.LOGIN, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_JOURNEY,
        featureGrouping: GTM_CONSTANTS.FEATURE_GROUPING_AUTH,
      }),
      step_name: 'Login Complete',
      feature_function: 'Login',
      method: 'email_password',
      user_id: uid,
    });
  }

  /**
   * Registration success — fire ONLY after the register API call returns OK.
   * @param inOrderJourney true when user registered as part of a package purchase (cart populated) → FIBRE_SALES_FUNNEL.
   *                       false (default) when standalone signup → FIBRE_JOURNEY.
   */
  trackRegister(inOrderJourney: boolean = false): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.REGISTER, {
      ...this.basePayload({
        funnelName: inOrderJourney
          ? GTM_CONSTANTS.FIBRE_SALES_FUNNEL
          : GTM_CONSTANTS.FIBRE_JOURNEY,
        featureGrouping: GTM_CONSTANTS.FEATURE_GROUPING_AUTH,
      }),
      step_name: 'Registration Complete',
      feature_function: 'Registration',
      method: 'email_password',
    });
  }

  trackSignUpPageView(): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.SIGN_UP, {
      ...this.basePayload({
        funnelName: GTM_CONSTANTS.FIBRE_JOURNEY,
        featureGrouping: GTM_CONSTANTS.FEATURE_GROUPING_AUTH,
      }),
      feature_function: 'Sign Up',
      method: 'email_password',
      user_status: 'registered',
    });
  }

  // ─── Home / Landing ───────────────────────────────────────────────────────

  trackPageView(
    pageUrl?: string,
    pageTitle?: string,
    dataLayerEvent: GtmDataLayerEvent = GtmDataLayerEvent.PageView,
  ): void {
    this._push(dataLayerEvent, {
      digital_channel: GTM_CONSTANTS.DIGITAL_CHANNEL,
      customer_type: GTM_CONSTANTS.CUSTOMER_TYPE,
      line_of_business: GTM_CONSTANTS.LINE_OF_BUSINESS,
      feature_grouping: GTM_CONSTANTS.FEATURE_GROUPING_SALES,
      feature_function: '',
      funnel_name: GTM_CONSTANTS.FIBRE_JOURNEY,
      page_location: pageUrl ?? (typeof window !== 'undefined' ? window.location.href : ''),
      page_title: pageTitle ?? (typeof document !== 'undefined' ? document.title : ''),
    });
  }

  trackCheckCoverageClick(coverageType?: string): void {
    this._push(GTM_EVENTS.CHECK_COVERAGE_CLICK, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_JOURNEY }),
      feature_function: 'Check Coverage',
      step_name: 'Coverage Check Initiated',
      ...(coverageType != null && { coverage_type: coverageType }),
    });
  }

  trackViewPackages(): void {
    this._push(GTM_EVENTS.VIEW_PACKAGES, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_JOURNEY }),
      step_name: 'View Packages',
      feature_function: 'Select Package',
    });
  }

  /**
   * Plan-type filter change on /packages.
   * - Argument keeps matrix copy (`Monthly` | `Prepaid`).
   * - dataLayer `plan_type` emits the spec values: `Postpaid` (for Monthly) or `Prepaid`.
   * - Selection is persisted so every downstream tag (cart → purchase) uses the same value.
   */
  trackSelectPlanType(planType: 'Monthly' | 'Prepaid'): void {
    const dataLayerPlanType: GtmDataLayerPlanType = planType === 'Monthly' ? 'Postpaid' : 'Prepaid';
    this.setPlanType(dataLayerPlanType);
    this._push(GTM_EVENTS.SELECT_PLAN_TYPE, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_JOURNEY }),
      step_name: 'View Packages',
      feature_function: 'Select Package',
      plan_type: dataLayerPlanType,
    });
  }

  // ─── Installation ─────────────────────────────────────────────────────────

  trackConfirmInstallDetails(installationType: string, deliveryAddressSameAsInstall: boolean, items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.CONFIRM_INSTALL_DETAILS, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Installation',
      feature_function: 'Installation Details',
      installation_type: installationType,
      delivery_address_same_as_install: deliveryAddressSameAsInstall,
    });
  }

  trackInstallDetailsSubmitted(installationType: string, deliveryAddressSameAsInstall: boolean, items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.INSTALL_DETAILS_SUBMITTED, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
      step_name: 'Installation Details Confirmed',
      feature_function: 'Installation Details',
      installation_type: installationType,
      delivery_address_same_as_install: deliveryAddressSameAsInstall,
    });
  }

  trackInstallationButton(btnType: 'changeAddressBtn' | 'backBtn', items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    if (btnType === 'changeAddressBtn') {
      this._push(GTM_EVENTS.CHANGE_ADDRESS_CLICK, {
        ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
        step_name: 'Installation',
        feature_function: 'Change Address',
      });
    } else {
      this.trackFunnelStepBack('Installation', 'Select Package', items);
    }
  }

  trackAddPaymentMethodClick(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.ADD_PAYMENT_METHOD_CLICK, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Checkout',
      feature_function: 'Add Payment Method',
    });
  }

  trackAddPaymentInfoCreditCard(items?: GtmEcommerceItem[]): void {
    this.updateSessionContext();
    this._push(GTM_EVENTS.ADD_PAYMENT_INFO, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items, usePageUrl: true }),
      step_name: 'Checkout',
      feature_function: 'Payment Method',
      payment_type: 'credit_card',
    });
  }

  trackConfirmOrder(items?: GtmEcommerceItem[], options?: { payment_type?: string; payment_gateway?: string }): void {
    this.updateSessionContext();
    const payment_type = options?.payment_type ?? 'debit_order';
    this._push(GTM_EVENTS.CONFIRM_ORDER, {
      ...this.basePayload({ funnelName: GTM_CONSTANTS.FIBRE_SALES_FUNNEL, items }),
      step_name: 'Checkout',
      feature_function: 'Confirm Order',
      payment_type,
      ...(payment_type === 'credit_card' && { payment_gateway: options?.payment_gateway ?? 'Netcash' }),
    });
  }

  // ─── Helpers (explicit methods so consumers see them on GtmService type) ───

  buildEcommerceItem(pkg: any, quantity = 1): GtmEcommerceItem {
    return buildEcommerceItem(pkg, quantity);
  }

  buildDiscountInfo(pkg: any): string {
    return buildDiscountInfo(pkg);
  }
}
